import { EDGE_TYPES } from 'components/tool/Facet/constant';
import { CreateSpecialEdge, LinesData, UpdateLineType, UpdateSpecialEdge } from './types';

export const initializeState = (): LinesData => ({
	activeRoofTool: false,
	activeEdgeTool: false,
	activeGutterLine: false,
	addGutterLine: false,
	removeGutterLine: false,
	currentFacetIndex: null,
	currentSelectedLine: null,
	hoverLinesIndex: [],
	selectedFacets: {},
	currentIndex: 0,
	linesHistory: [],
	createSpecialEdges: [],
	updateSpecialEdges: [],
	effectedFacets: [],
});

export const resetGutterLineState = (state: { data: Partial<LinesData> }) => {
	state.data.activeGutterLine = false;
	state.data.addGutterLine = false;
	state.data.removeGutterLine = false;
};

export const resetUnfinalizedState = (state: { data: LinesData }) => {
	state.data = initializeState();
};

const createSnapshot = (data: LinesData): string => {
	const { linesHistory, currentIndex, activeRoofTool, ...snapshot } = data;
	return JSON.stringify(snapshot);
};

export const updateState = (state: { data: LinesData }) => {
	if (state.data.currentIndex === 0 && state.data.linesHistory.length === 0) {
		state.data.linesHistory.push(createSnapshot(state.data));
	}
	const { activeRoofTool, currentIndex, linesHistory, ...rest } = state.data;

	const snapshot = createSnapshot(state.data);

	state.data.linesHistory = [
		...state.data.linesHistory.slice(0, state.data.currentIndex + 1),
		snapshot,
	];

	state.data.currentIndex = state.data.linesHistory.length - 1;
};

export type LineTypeUpdate = {
	updateLines: UpdateLineType[];
	createSpecialEdgesData: CreateSpecialEdge;
	updateSpecialEdgesData: UpdateSpecialEdge;
};

// applies an edge-type change on the selected facets and on the create/update special edge payloads.
// does not snapshot the history, the caller decides when to call `updateState`
export const applyLineTypeUpdate = (
	state: { data: LinesData },
	{ updateLines, createSpecialEdgesData, updateSpecialEdgesData }: LineTypeUpdate
) => {
	updateLines.forEach(({ id, lineType, facetId }) => {
		state.data.selectedFacets[facetId] ??= createInitialSelectedFacetVal();
		const facet = state.data.selectedFacets[facetId];

		const index = facet.selectedLines.findIndex(
			line => line.id === id
		);

		if (index !== -1) {
			facet.selectedLines[index].isSystemDetected = false;
			if (facet.selectedLines[index].lineType === lineType) return;
			if (lineType === EDGE_TYPES.NONE) {
				facet.selectedLines[index].lineType = '';
			} else {
				facet.selectedLines[index].lineType = lineType;
			}
		} else if (lineType !== EDGE_TYPES.NONE) {
			facet.selectedLines.push({ id, lineType, isSystemDetected: false });
		}
	});

	state.data.createSpecialEdges = filterCreateSpecialEdge(
		state.data.createSpecialEdges,
		createSpecialEdgesData
	);
	state.data.updateSpecialEdges = updateSpecialEdges(
		state.data.updateSpecialEdges,
		updateSpecialEdgesData
	);
};

export const filterCreateSpecialEdge = (
	existingEdges: CreateSpecialEdge[],
	newEdge: CreateSpecialEdge
): CreateSpecialEdge[] => {
	if (!newEdge?.edgeType || !newEdge?.edges?.length) return existingEdges;

	const newEdgeType = newEdge.edgeType;
	const newEdgeIds = newEdge.edges.map(
		(e) => `${e.roofSegmentId}:${e.roofSegmentEdgeId}`
	);
	const newKey = [newEdgeType, ...newEdgeIds].sort().join(',');
	const newEdgeSet = new Set(newEdgeIds);

	const filtered = existingEdges.filter((existing) => {
		const existingEdgeIds = existing.edges.map(
			(e) => `${e.roofSegmentId}:${e.roofSegmentEdgeId}`
		);
		const existingKey = [existing.edgeType, ...existingEdgeIds].sort().join(',');

		if (existingKey === newKey) return false;

		const existingEdgeSet = new Set(existingEdgeIds);
		const hasOverlap = Array.from(newEdgeSet).some((id) => existingEdgeSet.has(id));

		if (hasOverlap && existing.edgeType !== newEdgeType) return false;

		if (
			newEdge.edges.length === 1 &&
			existing.edges.length > 1 &&
			existingEdgeSet.has(newEdgeIds[0])
		) {
			return false;
		}

		return true;
	});

	return [...filtered, newEdge].filter(eachLine => eachLine.edgeType !== EDGE_TYPES.NONE);
};

export const updateSpecialEdges = (
	existingEdges: UpdateSpecialEdge[],
	updatedEdge: UpdateSpecialEdge
): UpdateSpecialEdge[] => {
	if (!updatedEdge?.edgeType || !updatedEdge?.edges?.length) return existingEdges;

	const updatedEdgeType = updatedEdge.edgeType;
	const updatedEdgeIds = updatedEdge.edges.map(
		(e) => `${e.roofSegmentId}:${e.roofSegmentEdgeId}`
	);
	const updatedKey = [updatedEdgeType, ...updatedEdgeIds].sort().join(',');
	const updatedEdgeSet = new Set(updatedEdgeIds);

	const filtered = existingEdges.filter((existing) => {
		const existingEdgeIds = existing.edges.map(
			(e) => `${e.roofSegmentId}:${e.roofSegmentEdgeId}`
		);
		const existingKey = [existing.edgeType, ...existingEdgeIds].sort().join(',');

		// Remove if same specialEdgeId (explicit replacement)
		if (
			updatedEdge.specialEdgeId != null &&
			existing.specialEdgeId === updatedEdge.specialEdgeId
		) {
			return false;
		}

		// Remove if same key (duplicate)
		if (existingKey === updatedKey) return false;

		// Remove if overlaps but edgeType differs
		const existingEdgeSet = new Set(existingEdgeIds);
		const hasOverlap = Array.from(updatedEdgeSet).some((id) => existingEdgeSet.has(id));
		if (hasOverlap && existing.edgeType !== updatedEdgeType) return false;

		// Remove if updating with single edge and existing has multiple and shares the ID
		if (
			updatedEdge.edges.length === 1 &&
			existing.edges.length > 1 &&
			existingEdgeSet.has(updatedEdgeIds[0])
		) {
			return false;
		}

		return true;
	});

	return [...filtered, updatedEdge];
};

//TODO: refactor along with "src/components/tool/Editor/ButtonDiv/Facet/EdgeTool/index.tsx" => handleRoofToolClick

export const createInitialSelectedFacetVal = () => ({
	selectedLines: [],
	newAddedGutterLines: [],
	removedGutterLineIds: [],
	gutterLines: []
});

export function buildInitialState(specialEdges: SpecialEdges[], gutterLines: GutterLine[]){

	const selectedFacets: LinesData['selectedFacets'] = {};
	// const draftSpecialEdges: UpdateLineType[] = [];
	const draftSpecialEdges: CreateSpecialEdge[] =[];

	for (const { type: edgeType, edges, isSystemDetected } of specialEdges) {
		const edgesMetaData: CreateSpecialEdge['edges'] = [];
		for (const [facetId, edgeID] of Object.entries(edges)) {
			edgesMetaData.push({roofSegmentEdgeId: edgeID, roofSegmentId: facetId});

			const facet = selectedFacets[facetId] ??= createInitialSelectedFacetVal();
			facet.selectedLines.push({ id: edgeID, lineType: edgeType, isSystemDetected });
		}

		if(isSystemDetected) continue;
		draftSpecialEdges.push({ 
			edgeType,
			edges: edgesMetaData,
		});
	}

	for(const gl of gutterLines){
		if(!gl.roofSegmentId) continue;

		if(gl.roofSegmentId in selectedFacets){
			selectedFacets[gl.roofSegmentId].gutterLines.push(gl);
			continue;
		}

		selectedFacets[gl.roofSegmentId] = createInitialSelectedFacetVal();

		selectedFacets[gl.roofSegmentId].gutterLines.push(gl);
	}
	return {draftSpecialEdges, selectedFacets	};
}

